/**
 * Applying a customer's brand colours without letting them break the page.
 *
 * 🔴 `tenant_branding.color_primary` and `color_accent` have been stored,
 * served by `/api/portal/overview` and typed in this client since the portal
 * was built, and NOTHING EVER APPLIED THEM. `theme.css` carries a `--brand`
 * token whose comment says it exists so per-tenant branding can override it;
 * no code overrode it. Every tenant's portal rendered in the same navy.
 *
 * That is the more interesting half of the bug: the hook, the column, the API
 * field and the type all existed, so every review of any one layer found it
 * present and correct. Only using it with two different tenants would show
 * that the colour never changes, and both seeded tenants rendered identically
 * so nothing looked wrong.
 *
 * ── Why a tenant colour cannot just be assigned ─────────────────────────
 *
 * The value is whatever somebody typed into a settings field. `#FFFF00` is a
 * legitimate brand colour and unreadable as text on white; `#0A0A0A` is
 * unreadable on the dark theme. A portal that renders a customer's support
 * link in their own yellow has not been white-labelled, it has been broken by
 * them, and they will report it as our fault.
 *
 * So each colour is used in one of two ways, and only one is trusted verbatim:
 *
 *   FILL   — a button, a bar: the colour shows at full strength and we choose
 *            a readable foreground to sit on it. One adjustment (077): a fill
 *            that would vanish into the page — Element Security's navy on the
 *            dark theme is 1.1:1, a slightly different black — is moved toward
 *            the page's opposite until it clears 3:1, and no further.
 *   TEXT   — a link or a figure: the colour is used ONLY if it clears the
 *            contrast bar against the page, and the theme's own colour is used
 *            when it does not.
 *
 * WCAG AA for normal text is 4.5:1. This targets it for text and 3:1 for a
 * fill against the page and for the foreground on it, which is the large-text
 * and UI-component threshold.
 *
 * Three colours (077): PRIMARY (fills, focus, brand-tinted text), ACCENT
 * (links and the support contact; text only) and HEADER (the top bar; a fill,
 * painted as given in both themes because it is the brand's bar and not the
 * theme's — a white bar keeps a dark logo visible at night).
 *
 * The server imports this file too, so Settings › Client portal can say which
 * colours will actually be used as text, and the portal re-exports it from
 * `apps/portal/src/brand.ts`. It lives under `src/` because that is what the
 * runtime image carries (2026-10-20). Nothing here may touch the DOM.
 */

/** `#RGB` or `#RRGGBB`, with or without the hash. Null for anything else. */
export function parseHex(value: string | null | undefined): [number, number, number] | null {
  if (typeof value !== 'string') return null;
  const hex = value.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

type RGB = [number, number, number];

const toHex = (rgb: RGB): string => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

/** WCAG relative luminance. */
export function luminance(rgb: RGB): number {
  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgb;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1..21. */
export function contrast(a: RGB, b: RGB): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

const WHITE: RGB = [255, 255, 255];
const BLACK: RGB = [0, 0, 0];

/**
 * The two page backgrounds, as `theme.css` paints them. Mirrored here rather
 * than read from the stylesheet because the server has no stylesheet;
 * `portal_brand.test.ts` holds the two in step.
 */
export const LIGHT_PAGE = '#f7f8fa';
export const DARK_PAGE = '#12161c';

/** Text that can be read on top of this colour. */
export function foregroundFor(rgb: RGB): string {
  return contrast(rgb, WHITE) >= contrast(rgb, BLACK) ? '#ffffff' : '#111111';
}

/**
 * A fill that can be seen against the page.
 *
 * Returns the colour unchanged when it already clears 3:1. Otherwise it is
 * mixed, in 5% steps, toward white on a dark page or black on a light one,
 * stopping at the first step that clears the bar — so a navy stays as navy as
 * it can rather than becoming grey. The hue is kept; only the lightness moves.
 */
export function liftForFill(rgb: RGB, page: RGB): RGB {
  const toward = luminance(page) > 0.5 ? BLACK : WHITE;
  let out = rgb;
  for (let t = 0.05; contrast(out, page) < 3 && t <= 1; t += 0.05) {
    out = rgb.map((c, i) => c + ((toward[i] as number) - c) * t) as RGB;
  }
  return out;
}

export interface BrandColours {
  primary?: string | null | undefined;
  accent?: string | null | undefined;
  header?: string | null | undefined;
}

export interface BrandVariables {
  /** The primary at full strength (lifted only if it would vanish), for fills. */
  '--brand': string;
  /** Readable text ON that fill. */
  '--brand-fg': string;
  /**
   * The primary for brand-tinted TEXT on the page — the tenant's value when
   * it is readable there, and the theme's own text colour when it is not.
   */
  '--brand-text': string;
  /** Links and the support contact: the accent when readable, else `--brand-text`. */
  '--accent-text': string;
  /** The top bar, painted as given. */
  '--header-bg': string;
  /** Readable text and icons on that bar. */
  '--header-fg': string;
}

/**
 * Resolve a tenant's colours into the variables the stylesheet consumes.
 *
 * `pageBackground` is the resolved `--bg` for the theme in force, so the same
 * colour can be trusted for text in light and rejected in dark, or the reverse.
 * Only the variables with a usable colour behind them are returned, so a
 * tenant with a primary and nothing else leaves the stylesheet's own accent
 * and header defaults in place. Null when nothing at all is usable, and the
 * caller then writes nothing.
 */
export function brandVariables(colours: BrandColours, pageBackground: string): Partial<BrandVariables> | null {
  const page = parseHex(pageBackground) ?? WHITE;
  const out: Partial<BrandVariables> = {};

  const primary = parseHex(colours.primary);
  if (primary !== null) {
    const fill = liftForFill(primary, page);
    out['--brand'] = toHex(fill);
    out['--brand-fg'] = foregroundFor(fill);
    // 4.5:1 against the page, or we do not use it for text at all. Falling
    // back to `currentColor` rather than to a hex keeps the theme in charge:
    // whatever the page already uses for text is by definition readable on it.
    out['--brand-text'] = contrast(primary, page) >= 4.5 ? toHex(primary) : 'currentColor';
  }

  const accent = parseHex(colours.accent);
  if (accent !== null) {
    out['--accent-text'] = contrast(accent, page) >= 4.5 ? toHex(accent) : (out['--brand-text'] ?? 'currentColor');
  }

  const header = parseHex(colours.header);
  if (header !== null) {
    out['--header-bg'] = toHex(header);
    out['--header-fg'] = foregroundFor(header);
  }

  return Object.keys(out).length === 0 ? null : out;
}

/**
 * Whether a colour will be used as text on each theme, for the settings
 * screen: a colour saved and then quietly not used is the thing 051 warns
 * about. Null when the value is not a colour.
 */
export function textReadability(value: string | null | undefined): { light: boolean; dark: boolean } | null {
  const rgb = parseHex(value);
  if (rgb === null) return null;
  return {
    light: contrast(rgb, parseHex(LIGHT_PAGE) as RGB) >= 4.5,
    dark: contrast(rgb, parseHex(DARK_PAGE) as RGB) >= 4.5,
  };
}

/* ── The admin console (2026-10-20) ───────────────────────────────────────
 *
 * The owner signed in as Element Security's owner and expected Element's
 * colours in the console; until now only the portal applied them. The console
 * is a different page with different tokens, so it gets its own mapping from
 * the same three colours and the same arithmetic:
 *
 *   PRIMARY → every token CosMon paints orange (`theme.css`): `--accent` and
 *             its hover, quiet tint and foreground — primary buttons, the
 *             current nav item, selected chips and stat tiles, tabs, toggles,
 *             focus rings, sort arrows, meters, the rail's mark — as a FILL,
 *             lifted only when it would vanish into the page (Element's navy
 *             on the dark console is 1.1:1); and `--link`, as TEXT.
 *   ACCENT  → nothing here. The owner chose (2026-10-20) that the console is
 *             the tenant's primary wherever it is CosMon's orange; the accent
 *             is the portal's links and support contact.
 *   HEADER  → `--brand-plate`, the plate the logo sits on in the rail, and
 *             nowhere else. The console is dense and dark; a white topbar
 *             across it would be the loudest thing on the screen and would
 *             wash out the status colours the screen exists to show.
 *
 * One deliberate difference from the portal: a text colour that fails 4.5:1
 * is LIFTED until it passes rather than replaced by `currentColor`. The
 * console's links are not underlined, so a link in the body text colour stops
 * being visibly a link; a lighter red is still Element's red and still a link.
 * Either way no unreadable text reaches the page, which is the rule.
 *
 * The status colours are untouched: brand never means healthy or broken.
 */

/** The console's two page backgrounds, as apps/admin/src/theme.css paints them. */
export const CONSOLE_DARK_PAGE = '#0d1117';
export const CONSOLE_LIGHT_PAGE = '#f4f6f8';

/** Move a colour toward the page's opposite until it clears `ratio` against the page. */
function liftTo(rgb: RGB, page: RGB, ratio: number): RGB {
  const toward = luminance(page) > 0.5 ? BLACK : WHITE;
  let out = rgb;
  for (let t = 0.05; contrast(out, page) < ratio && t <= 1; t += 0.05) {
    out = rgb.map((c, i) => c + ((toward[i] as number) - c) * t) as RGB;
  }
  return out;
}

/**
 * How strong the selected-state tint (`--accent-quiet`: the current nav item, a
 * selected chip or stat tile, the palette's highlight) must be to show as much
 * as CosMon's own orange tint does on the same page.
 *
 * CosMon's fixed 14% made Element's navy tint 1.14:1 on the dark console —
 * present and unseen, and the owner reported the brand as "only the logo and
 * New asset" (2026-10-20). Bright orange shows at 14%; a dark brand needs more.
 * The alpha rises in 1% steps from 6% until the tint stands off the page as
 * far as the orange's does (`theme.css`: 14% dark, 12% light), capped at 40%.
 */
function quietAlpha(fill: RGB, page: RGB): number {
  const tint = (rgb: RGB, a: number): RGB => rgb.map((c, i) => c * a + (page[i] as number) * (1 - a)) as RGB;
  const light = luminance(page) > 0.5;
  const target = contrast(tint([255, 124, 0], light ? 0.12 : 0.14), page);
  let alpha = 0.06;
  while (contrast(tint(fill, alpha), page) < target && alpha < 0.4) alpha = Math.round((alpha + 0.01) * 100) / 100;
  return alpha;
}

/**
 * Every variable the console's branding writes. The shell clears exactly
 * these when the next tenant has none, so a brand cannot outlive its tenant.
 */
export const CONSOLE_BRAND_PROPERTIES = [
  '--accent',
  '--accent-hover',
  '--accent-quiet',
  '--accent-text',
  '--link',
  '--brand-plate',
] as const;

export type ConsoleBrandVariables = Partial<Record<(typeof CONSOLE_BRAND_PROPERTIES)[number], string>>;

/**
 * Resolve a tenant's colours into the console's variables, for the page
 * background in force. Only variables with a usable colour behind them are
 * returned; null when there are none, and the console keeps CosMon's look.
 */
export function consoleBrandVariables(colours: BrandColours, pageBackground: string): ConsoleBrandVariables | null {
  const page = parseHex(pageBackground) ?? parseHex(CONSOLE_DARK_PAGE) as RGB;
  const out: ConsoleBrandVariables = {};

  const primary = parseHex(colours.primary);
  if (primary !== null) {
    const fill = liftForFill(primary, page);
    // Hover moves the same way the lift does: lighter on dark, darker on light.
    const toward = luminance(page) > 0.5 ? BLACK : WHITE;
    const hover = fill.map((c, i) => c + ((toward[i] as number) - c) * 0.12) as RGB;
    out['--accent'] = toHex(fill);
    out['--accent-hover'] = toHex(hover);
    out['--accent-quiet'] = `rgba(${fill.map((c) => Math.round(c)).join(', ')}, ${quietAlpha(fill, page)})`;
    out['--accent-text'] = foregroundFor(fill);
  }

  // Links too: the owner chose (2026-10-20) that every place CosMon is orange
  // takes the primary. The accent stays the portal's.
  if (primary !== null) out['--link'] = toHex(liftTo(primary, page, 4.5));

  const header = parseHex(colours.header);
  if (header !== null) {
    out['--brand-plate'] = toHex(header);
  }

  return Object.keys(out).length === 0 ? null : out;
}
